import type { Health } from "../lib/types";

type Tone = "ok" | "warn" | "danger" | "neutral";

const TONE_CLASS: Record<Tone, string> = {
  ok: "bg-ok-soft text-ok",
  warn: "bg-warn-soft text-warn",
  danger: "bg-danger-soft text-danger",
  neutral: "bg-surface-100 text-ink-muted",
};

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: Tone;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[tone]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

// `pending` is deliberately NEUTRAL, not danger: a grant that the cloud hasn't
// finished applying is not a broken connection, and badging it "Access denied"
// (as we used to) tells the customer their setup failed when it hasn't. It also
// reads as permanent, which is how a self-healing state ended up looking like an
// outage on Aurora's first test.
const HEALTH: Record<Health, { tone: Tone; label: string }> = {
  ok: { tone: "ok", label: "Healthy" },
  pending: { tone: "neutral", label: "Finishing setup" },
  denied: { tone: "danger", label: "Access denied" },
  missing: { tone: "warn", label: "Not found" },
  unchecked: { tone: "neutral", label: "Not checked" },
};

// Shared by connectors and destinations (both use the Health enum).
export function StatusBadge({ health }: { health: Health }) {
  const { tone, label } = HEALTH[health] ?? HEALTH.unchecked;
  return <Badge tone={tone}>{label}</Badge>;
}
